import type { Calendar, CalendarEntry, TripMode, TripState, TripTotals } from '@/types';
import { STATIONS, STATION_INDEX } from '@/data/graph';
import { findPath } from './pathfinding';
import { haversine } from './geo';
import {
  CO2_TRAIN,
  CO2_BUS,
  NIGHT_TRAIN_DEPARTURE_HOUR,
  NIGHT_SEGMENT_MIN_DURATION,
} from './constants';

/**
 * Parse une chaîne "YYYY-MM-DD" + "HH:MM" en `Date` locale.
 * Renvoie `null` si la date est absente.
 */
export function parseStart(
  dateStr: string | null,
  timeStr: string | undefined,
): Date | null {
  if (!dateStr) return null;
  const [y, m, d] = dateStr.split('-').map(Number);
  const [hh, mm] = (timeStr || '09:00').split(':').map(Number);
  return new Date(y, m - 1, d, hh, mm, 0, 0);
}

/** Renvoie une nouvelle date décalée de `min` minutes */
export function addMinutes(date: Date, min: number): Date {
  return new Date(date.getTime() + min * 60_000);
}

/**
 * Si le segment est un train de nuit, cale le départ à la `NIGHT_TRAIN_DEPARTURE_HOUR`
 * du jour courant (sauf si on est déjà après cette heure, auquel cas on garde l'heure courante).
 */
export function adjustForNightTrain(date: Date, isNight: boolean): Date {
  if (!isNight) return date;
  const cur = new Date(date.getTime());
  if (cur.getHours() < NIGHT_TRAIN_DEPARTURE_HOUR) {
    cur.setHours(NIGHT_TRAIN_DEPARTURE_HOUR, 0, 0, 0);
  }
  return cur;
}

/**
 * Validation légère d'une chaîne "HH:MM".
 * Renvoie la chaîne d'origine si valide, sinon `null`.
 */
function parseTimeString(t: string | undefined | null): string | null {
  if (!t) return null;
  return /^\d{2}:\d{2}$/.test(t) ? t : null;
}

/**
 * Calcule le calendrier complet de l'itinéraire en fonction de l'état.
 *
 * Pour chaque étape, renvoie :
 * - `arrive` : date d'arrivée (null si pas de date de départ globale)
 * - `depart` : date de départ vers l'étape suivante (null sur la dernière)
 * - `nights` : nuits sur place
 * - `segmentPath` : trajet calculé vers la suivante (ou null si non desservi)
 *
 * Overrides par étape (RouteStop) :
 * - `stop.startTime`     → remplace `tripStartTime` pour le départ depuis CETTE étape
 * - `stop.modeOverride`  → remplace `routeMode` pour le segment partant de cette étape
 *
 * Plus des totaux agrégés : durée totale, km, CO2, nuits, etc.
 *
 * IMPORTANT : la fonction est PURE, elle ne mute pas `state`.
 */
export function buildCalendar(state: TripState): Calendar {
  const startDate = parseStart(state.tripStartDate, state.tripStartTime);
  const cal: CalendarEntry[] = [];
  let current = startDate ? new Date(startDate.getTime()) : null;

  let totalMin = 0;
  let totalKm = 0;
  let totalSegs = 0;
  let totalDirects = 0;
  let totalNights = 0;
  let totalCO2 = 0;
  let trainMin = 0;
  let busMin = 0;
  let nightTrainMin = 0;
  let nightsSavedByTrain = 0;
  let unreachable = false;

  // Cas particulier de la 1ʳᵉ étape : l'override d'heure s'applique aussi
  // à la date de départ globale (sinon on serait en décalage avec
  // `calendar.startDate` qui est calculée à part).
  if (current && state.routeStops.length > 0) {
    const firstStop = state.routeStops[0];
    const overrideFirst = parseTimeString(firstStop.startTime);
    if (overrideFirst) {
      const [hh, mm] = overrideFirst.split(':').map(Number);
      current.setHours(hh, mm, 0, 0);
    }
  }

  for (let i = 0; i < state.routeStops.length; i++) {
    const stop = state.routeStops[i];
    const isLast = i === state.routeStops.length - 1;
    const nights = stop.nights || 0;

    const arrive = current ? new Date(current.getTime()) : null;

    let departFromHere: Date | null = null;
    let segPath = null;
    let kmThisLeg = 0;

    if (!isLast) {
      const nextStop = state.routeStops[i + 1];
      const nextS = STATIONS[STATION_INDEX[nextStop.id]];
      const s = STATIONS[STATION_INDEX[stop.id]];
      kmThisLeg = haversine(s.coords, nextS.coords);
      totalKm += kmThisLeg;

      // Mode appliqué à CE segment : override de l'étape > mode global.
      const segmentMode: TripMode = stop.modeOverride ?? state.routeMode;
      segPath = findPath(stop.id, nextStop.id, segmentMode);
      if (!segPath) {
        unreachable = true;
      } else {
        totalMin += segPath.total;
        totalSegs++;
        if (segPath.segments.length === 1) totalDirects++;

        // Heures par mode + CO2 — réparti au prorata du temps de chaque sous-segment
        const totalSegMin = segPath.total;
        segPath.segments.forEach((seg) => {
          if (seg.mode === 'train') {
            trainMin += seg.cost;
            if (seg.night) {
              nightTrainMin += seg.cost;
              if (seg.cost >= NIGHT_SEGMENT_MIN_DURATION) nightsSavedByTrain++;
            }
            if (seg.cost > 0 && totalSegMin > 0) {
              totalCO2 += kmThisLeg * (seg.cost / totalSegMin) * CO2_TRAIN;
            }
          } else if (seg.mode === 'bus') {
            busMin += seg.cost;
            if (seg.cost > 0 && totalSegMin > 0) {
              totalCO2 += kmThisLeg * (seg.cost / totalSegMin) * CO2_BUS;
            }
          }
        });
      }

      // Calcul du départ depuis cette étape (après les nuits sur place).
      // Heure de départ : override de l'étape > heure globale.
      // Pour la 1ère étape, on garde l'heure exacte (déjà appliquée plus haut sur `current`).
      if (current) {
        const effectiveTime =
          parseTimeString(stop.startTime) ?? state.tripStartTime ?? '09:00';

        let departCandidate: Date;
        if (i === 0) {
          // Première étape : on a déjà appliqué l'override sur `current` plus haut.
          // On part exactement à cette heure-là.
          departCandidate = new Date(current.getTime());
        } else {
          departCandidate = new Date(current.getTime());
          departCandidate.setDate(departCandidate.getDate() + nights);
          const [hh, mm] = effectiveTime.split(':').map(Number);
          departCandidate.setHours(hh, mm, 0, 0);
        }

        // Si le premier segment est un train de nuit, on cale à 21h.
        const dominantNight = segPath && segPath.segments[0] && segPath.segments[0].night;
        if (dominantNight) {
          departCandidate = adjustForNightTrain(departCandidate, true);
        }

        departFromHere = departCandidate;

        // Avance "current" : départ + durée totale du segment
        current = segPath
          ? addMinutes(departCandidate, segPath.total)
          : departCandidate;
      }
    }

    totalNights += nights;

    cal.push({
      arrive,
      depart: departFromHere,
      nights,
      segmentPath: segPath,
    });
  }

  const totals: TripTotals = {
    totalMin,
    totalKm,
    totalSegs,
    totalDirects,
    totalNights,
    totalCO2: Math.round(totalCO2),
    trainMin,
    busMin,
    nightTrainMin,
    nightsSavedByTrain,
    unreachable,
  };

  return {
    cal,
    hasDate: !!startDate,
    startDate,
    endDate: current,
    totals,
  };
}
